'use client';

import { useEffect } from 'react';
import { FOOTER_LINKS, FOOTER_TEXT } from 'utils/constants';
import Link from 'next/link';
import React from 'react';
import { SocialMediaLinks } from '../cards/social-media/links';
import { ContactUsCard } from '../cards/contact-us';
import Text from '../common/Text';
import { CardBody, CardFooter, CardHeader, Cards } from '../card';
import { Button } from '../button';
import { useRouter } from 'next/navigation';
import './component.scss';
import LocationWidget from '../location/locationWidget';
import { StickyFooter } from './sticky-footer';
import { StickyFooterData } from '../section/main';
import MadeWithLove from './made-with-love';

interface FooterProps {
  slug?: string;
  color?: string;
  stickyFooterName?: string;
  data?: StickyFooterData;
}

const splitLinksIntoColumns = (
  linksArray: { href: string; text: string; target?: string }[]
) => [linksArray.slice(0, 7), linksArray.slice(7)];

export const Footer = ({
  slug,
  color,
  stickyFooterName: sickyFooterName = 'WhereToBuy',
  data,
}: FooterProps) => {
  const router = useRouter();
  const columns = splitLinksIntoColumns(FOOTER_LINKS);

  useEffect(() => {
    const ecoLinkStyles = `
      .eco-m .eco-c a {
        cursor: pointer;
        color: ${color || '#1c8eff'}
      }
    `;
    const styleElement = document.createElement('style');
    styleElement.type = 'text/css';
    styleElement.innerHTML = ecoLinkStyles;

    document.body.appendChild(styleElement);

    return () => {
      document.body.removeChild(styleElement);
    };
  }, [color]);

  return (
    <footer className='relative overflow-hidden bg-white'>
      <div className='mx-auto w-full max-w-[90rem] px-5 pt-16 sm:px-6 lg:px-[120px]'>
        <div className='flex flex-col gap-y-16 sm:gap-y-20'>
          <div className='grid grid-cols-2 gap-4 sm:grid-cols-4 sm:gap-9'>
            <ContactUsCard classNames='col-span-2 sm:col-span-2 md:col-span-2 lg:col-span-1' />
            <Cards className='mrc-contact-us-card col-span-2 border bg-white p-6 text-center sm:col-span-2 md:col-span-2 lg:col-span-1'>
              <CardHeader className='card-header'>Become a dealer</CardHeader>
              <CardBody className='card-body'>
                <Text size='paragraph'>
                  Expand opportunities. Join our robust ecosystem.
                </Text>
              </CardBody>
              <CardFooter>
                <Button
                  variant='custom'
                  className='card-footer'
                  onClick={() => router.push('/support/dealer-signup')}
                >
                  Learn more
                </Button>
              </CardFooter>
            </Cards>

            {columns.map((column, colIndex) => (
              <div key={colIndex} className='col-span-2 flex sm:col-span-1'>
                <div className='flex flex-col gap-y-5'>
                  {column.map((link, i) => (
                    <div key={i}>
                      <Link
                        href={{ pathname: link.href }}
                        scroll={true}
                        target={link.target || '_self'}
                        className='inline-flex gap-x-2 text-[15px] text-gray-700 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600'
                      >
                        {link.text}
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className='flex flex-col gap-y-6'>
            <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between'>
              <Link
                href={{ pathname: '/' }}
                scroll={true}
                className='flex-none'
              >
                <img
                  src='/images/mrcool-logo.svg'
                  alt='logo'
                  className='w-full max-w-40'
                />
              </Link>

              <div className='mt-9 sm:ml-9 sm:mt-0'>
                <LocationWidget ignoreTheme={true} />
              </div>

              <div className='mt-9 flex items-center gap-2 sm:mt-0 sm:justify-center'>
                <div className='grid grid-cols-6 gap-3'>
                  <SocialMediaLinks classNames='social-footer mrc-social-links' />
                </div>
              </div>
            </div>
            <div className='pb-12  pt-6'>
              <div className='flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between'>
                <div className='text-sm text-gray-600 dark:text-gray-400'>
                  {FOOTER_TEXT.copyright}
                </div>

                <div>
                  <MadeWithLove />
                </div>

                <div className='mb-5 flex flex-col gap-4 text-sm sm:gap-8 lg:mb-0 lg:flex-row'>
                  <Link
                    className='text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600 sm:inline-flex'
                    href={{ pathname: '/privacy-policy' }}
                  >
                    {FOOTER_TEXT.privacyPolicy}
                  </Link>
                  <Link
                    className='text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600 sm:inline-flex'
                    href={{ pathname: '/terms-and-conditions' }}
                  >
                    {FOOTER_TEXT.termsAndConditions}
                  </Link>
                  <Link
                    className='text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600 sm:inline-flex'
                    href={{ pathname: '/warranty-disclaimer' }}
                  >
                    Warranty Disclaimer
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {slug && <StickyFooter name={sickyFooterName} slug={slug} data={data} />}
    </footer>
  );
};

export default Footer;
